import { z } from "zod";
import { DEFAULT_DURATION_MIN, MAX_DURATION_MIN } from "./appointment.js";
import { dayOf, formatTimeOfDay, isTimeOfDay, isoWeekdayOf, timeOf } from "./period.js";
/**
 * THE AGENDA, AS ARITHMETIC (spec 2026-09-17, §5). The app WARNS about an
 * overlap or a time outside the hours and never prevents it: a clinic that
 * overbooks on purpose must not fight the app. The server checks none of it.
 *
 * Every interval is half-open, [dueAt, endsAt): 9:00-9:30 and 9:30-10:00
 * touch and do not overlap.
 */
const MINUTE = 60_000;
/** `dueAt + durationMin`. An appointment always has a `dueAt` (0078's check). */
export function endsAt(a) {
    return new Date(Date.parse(a.dueAt ?? "") + a.durationMin * MINUTE).toISOString();
}
function span(a) {
    const start = Date.parse(a.dueAt ?? "");
    return [start, start + a.durationMin * MINUTE];
}
function professionals(a) {
    return a.assignees.map((x) => x.employeeId);
}
/** Those of `others` that collide with `a`: a professional in common, none cancelled, the intervals cross. `a` itself never counts. */
export function overlapping(a, others) {
    const mine = professionals(a);
    const [start, end] = span(a);
    return others.filter((o) => {
        if (o.id === a.id || o.status === "CANCELLED")
            return false;
        if (!professionals(o).some((id) => mine.includes(id)))
            return false;
        const [oStart, oEnd] = span(o);
        return start < oEnd && oStart < end;
    });
}
/** The range `employeeId` works on `day`, or null when that day is not one of theirs. */
export function hoursOn(hours, employeeId, day) {
    const weekday = isoWeekdayOf(day);
    return hours.find((h) => h.employeeId === employeeId && h.weekday === weekday) ?? null;
}
/**
 * Whether [dueAt, endsAt) falls outside its professional's range that day, read
 * in the BUSINESS's zone. No hours at all never warns (nothing was said); hours
 * on other days but not this one warn (that day is not worked).
 */
export function outsideHours(a, hours, tz) {
    const mine = professionals(a);
    const theirs = hours.filter((h) => mine.includes(h.employeeId));
    if (theirs.length === 0)
        return false;
    const day = dayOf(a.dueAt ?? "", tz);
    const range = theirs.find((h) => h.weekday === isoWeekdayOf(day));
    if (range === undefined)
        return true;
    const end = endsAt(a);
    // Ending on another day is past any closing time.
    if (dayOf(end, tz) !== day && timeOf(end, tz) !== "00:00")
        return true;
    const endTime = dayOf(end, tz) === day ? timeOf(end, tz) : "24:00";
    return timeOf(a.dueAt ?? "", tz) < range.opens || endTime > range.closes;
}
/** Whether this person attends appointments: active, with `worksModule === "citas"`. */
export function attendsAppointments(e) {
    return e.active && e.worksModule === "citas";
}
/** Who attends appointments (`attendsAppointments`). */
export function professionalsOf(staff) {
    return staff.filter(attendsAppointments);
}
/** The length an appointment proposes from its lines: their durations times quantity, 30 with nothing to add, capped at 480. */
export function proposedDuration(lines) {
    const total = lines.reduce((sum, line) => sum + (line.durationMin ?? 0) * line.quantity, 0);
    if (total <= 0)
        return DEFAULT_DURATION_MIN;
    return Math.min(total, MAX_DURATION_MIN);
}
const DEFAULT_OPENS = 7 * 60;
const DEFAULT_CLOSES = 19 * 60;
/** "HH:MM" as minutes since midnight. */
export function minutesOf(time) {
    const [hour = 0, minute = 0] = time.split(":").map(Number);
    return hour * 60 + minute;
}
/** Minutes since midnight as "HH:MM". */
export function clockOf(minutes) {
    return `${String(Math.floor(minutes / 60)).padStart(2, "0")}:${String(minutes % 60).padStart(2, "0")}`;
}
/**
 * One day of the agenda laid out on its axis: `top` and `height` in minutes
 * from `opens`. The axis is the professional's range, 07:00-19:00 with none,
 * and WIDENS (to the half hour) when a block falls outside it -- a block is
 * never hidden. Blocks that overlap in time sit side by side (greedy columns
 * per group); `overlaps` is the warning, which needs a professional in common.
 */
export function dayAgenda(appts, day, hours, tz) {
    const shown = appts
        .filter((a) => a.status !== "CANCELLED" && a.dueAt !== null && dayOf(a.dueAt, tz) === day)
        .map((a) => {
        const start = minutesOf(timeOf(a.dueAt ?? "", tz));
        return { a, start, end: Math.min(start + (a.durationMin || DEFAULT_DURATION_MIN), 24 * 60) };
    })
        .sort((x, y) => x.start - y.start || x.end - y.end);
    let opens = hours === null ? DEFAULT_OPENS : minutesOf(hours.opens);
    let closes = hours === null ? DEFAULT_CLOSES : minutesOf(hours.closes);
    for (const { start, end } of shown) {
        opens = Math.min(opens, Math.floor(start / 30) * 30);
        closes = Math.max(closes, Math.ceil(end / 30) * 30);
    }
    const blocks = [];
    let group = [];
    let groupEnd = -1;
    const closeGroup = () => {
        const columns = group.reduce((most, g) => Math.max(most, g.block.column + 1), 0);
        for (const g of group)
            g.block.columns = columns;
        group = [];
    };
    for (const { a, start, end } of shown) {
        if (start >= groupEnd)
            closeGroup();
        // The first column whose last block has already ended.
        const taken = new Set(group.filter((g) => g.end > start).map((g) => g.block.column));
        let column = 0;
        while (taken.has(column))
            column += 1;
        const block = {
            id: a.id,
            top: start - opens,
            height: end - start,
            column,
            columns: 1,
            overlaps: overlapping(a, appts).length > 0,
        };
        blocks.push(block);
        group.push({ block, end });
        groupEnd = Math.max(groupEnd, end);
    }
    closeGroup();
    return { opens: clockOf(opens), closes: clockOf(closes), blocks };
}
/** The ISO weekday's name, 1 = «Lunes». */
export const WEEKDAY_NAMES = {
    1: "Lunes",
    2: "Martes",
    3: "Miércoles",
    4: "Jueves",
    5: "Viernes",
    6: "Sábado",
    7: "Domingo",
};
/** The seven days, Monday first; a day nothing was said about is «No atiende», with 08:00-17:00 ready to switch on. */
export function weekRows(hours) {
    return [1, 2, 3, 4, 5, 6, 7].map((weekday) => {
        const range = hours.find((h) => h.weekday === weekday);
        return range === undefined
            ? { weekday, works: false, opens: "08:00", closes: "17:00" }
            : { weekday, works: true, opens: range.opens, closes: range.closes };
    });
}
/** What `PUT /agenda/hours/:employeeId` and `PUT /business/hours` are sent: the days worked, or why not. */
export function hoursToSave(rows) {
    const worked = rows.filter((r) => r.works);
    const bad = worked.find((r) => r.opens >= r.closes);
    if (bad !== undefined)
        return { ok: false, message: `El ${(WEEKDAY_NAMES[bad.weekday] ?? "").toLowerCase()} cierra antes de abrir.` };
    return { ok: true, hours: worked.map(({ weekday, opens, closes }) => ({ weekday, opens, closes })) };
}
/**
 * THE HOURS A TIME PICKER LISTS (the web's `TimeField`, the phone's hours
 * sheet): `from` to `to`, both included, every `step` minutes. A saved `value`
 * off the steps or outside the range (an order «para las 9:07», a closing at
 * 23:00) joins the list in its place, so the picker shows it chosen and a
 * second look does not lose it.
 */
export function timeSlots({ from, to, step, value }) {
    const slots = [];
    for (let at = minutesOf(from); at <= minutesOf(to); at += step)
        slots.push(clockOf(at));
    if (isTimeOfDay(value) && !slots.includes(value)) {
        slots.push(value);
        slots.sort((a, b) => minutesOf(a) - minutesOf(b));
    }
    return slots;
}
/**
 * The body of `PUT /agenda/hours/:employeeId` and `PUT /business/hours`: the
 * whole week, never a diff. The shapes only; `weekHoursProblem` says what is
 * wrong with them, each in its own sentence.
 */
export const weekHoursBodySchema = z
    .object({
    hours: z.array(z.object({ weekday: z.int(), opens: z.string(), closes: z.string() }).strict()).max(7),
})
    .strict();
/**
 * Why a week of hours cannot be saved, or null: what both PUTs refuse. One
 * range per day is V1's rule, here and not in the schemas, whose keys admit
 * a second range: a lunch break later is this rule and a screen, never a
 * migration.
 */
export function weekHoursProblem(hours) {
    const seen = new Set();
    for (const range of hours) {
        if (!Number.isInteger(range.weekday) || range.weekday < 1 || range.weekday > 7)
            return "El día no es válido.";
        if (!isTimeOfDay(range.opens) || !isTimeOfDay(range.closes))
            return "La hora no es válida.";
        if (range.opens >= range.closes)
            return "La hora de cierre va después de la de apertura.";
        if (seen.has(range.weekday))
            return "Un solo horario por día.";
        seen.add(range.weekday);
    }
    return null;
}
/** A `time` column as postgres.js hands it back, "HH:MM:SS", in the wire's "HH:MM". */
export function sqlTimeOfDay(time) {
    return time.slice(0, 5);
}
/**
 * INICIO'S LINE ABOUT THE DAY (spec §6.4): «Hoy: 6 citas · próxima a las 9:30».
 * `appts` is today's agenda (not cancelled); «next» is the first still booked
 * that has not started.
 */
export function agendaTodayLine(appts, now, tz) {
    const shown = appts.filter((a) => a.status !== "CANCELLED");
    if (shown.length === 0)
        return "Hoy no hay citas";
    const count = `Hoy: ${String(shown.length)} ${shown.length === 1 ? "cita" : "citas"}`;
    const next = shown
        .filter((a) => a.status === "PENDING" && a.dueAt !== null && Date.parse(a.dueAt) >= Date.parse(now))
        .sort((x, y) => Date.parse(x.dueAt ?? "") - Date.parse(y.dueAt ?? ""))[0];
    return next === undefined ? count : `${count} · próxima a las ${formatTimeOfDay(timeOf(next.dueAt ?? "", tz))}`;
}
